import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import './HRLeaves.css';

const CITIES = ['All Cities', 'Lahore', 'Karachi', 'Islamabad', 'Multan'];

const STATUS_FILTERS = [
  { key: 'pending',  label: '● Pending' },
  { key: 'approved', label: '✓ Approved' },
  { key: 'rejected', label: '✕ Rejected' },
  { key: 'all',      label: 'All' },
];

const LEAVE_TYPE_COLORS = {
  casual: 'lt-casual',
  sick:   'lt-sick',
  annual: 'lt-annual',
};

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

function getAvatarClass(name) {
  const c = ['lv-av-blue','lv-av-teal','lv-av-amber','lv-av-purple','lv-av-coral','lv-av-green'];
  let h = 0;
  for (let i = 0; i < (name||'').length; i++) h += name.charCodeAt(i);
  return c[h % c.length];
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-PK', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
}

function formatDateRange(from, to) {
  const f = formatDate(from);
  const t = formatDate(to);
  return from === to ? f : `${f} — ${t}`;
}

function daysSince(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (diff === 0) return 'today';
  if (diff === 1) return '1 day ago';
  return `${diff} days ago`;
}

// ─── REJECT MODAL ─────────────────────────────────────────────────────────────

function RejectModal({ leave, onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (!reason.trim()) { setError('Please provide a rejection reason.'); return; }
    setLoading(true);
    await onConfirm(leave.id, reason);
    setLoading(false);
  };

  return (
    <div className="lv-modal-overlay" onClick={onClose}>
      <div className="lv-modal" onClick={e => e.stopPropagation()}>
        <div className="lv-modal-header">
          <h2>Reject Leave Request</h2>
          <button className="lv-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="lv-modal-body">
          <div className="lv-reject-info">
            <strong>{leave.full_name}</strong> requested{' '}
            <span className={`lv-type-tag ${LEAVE_TYPE_COLORS[leave.leave_type] || ''}`}>
              {leave.leave_type} leave
            </span>{' '}
            for {leave.total_days} day(s) — {formatDateRange(leave.from_date, leave.to_date)}
          </div>
          {error && <div className="lv-modal-error">{error}</div>}
          <div className="lv-form-group">
            <label>Rejection Reason *</label>
            <textarea
              rows="3"
              placeholder="Explain why this leave is being rejected…"
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
          </div>
        </div>
        <div className="lv-modal-footer">
          <button className="lv-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="lv-btn-reject" onClick={handleConfirm} disabled={loading}>
            {loading ? 'Rejecting…' : 'Reject Leave'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LEAVE CARD (pending) ─────────────────────────────────────────────────────

function PendingCard({ leave, onApprove, onReject, approving }) {
  return (
    <div className="lv-pending-card">
      <div className="lv-pending-left">
        <div className={`lv-avatar ${getAvatarClass(leave.full_name)}`}>
          {getInitials(leave.full_name)}
        </div>
        <div className="lv-pending-info">
          <span className="lv-pending-name">{leave.full_name}</span>
          <span className="lv-pending-meta">
            {leave.emp_code}
            {leave.department ? ` · ${leave.department}` : ''}
            {leave.location_name ? ` · ${leave.location_name}` : ''}
          </span>
          <div className="lv-pending-details">
            <span className={`lv-type-tag ${LEAVE_TYPE_COLORS[leave.leave_type] || ''}`}>
              {leave.leave_type?.charAt(0).toUpperCase() + leave.leave_type?.slice(1)} Leave
            </span>
            <span className="lv-pending-days">{leave.total_days} day(s)</span>
            <span className="lv-pending-dates">{formatDateRange(leave.from_date, leave.to_date)}</span>
          </div>
          {leave.reason && (
            <span className="lv-pending-reason">"{leave.reason}"</span>
          )}
        </div>
      </div>
      <div className="lv-pending-right">
        <span className="lv-since">{daysSince(leave.created_at)}</span>
        <div className="lv-pending-actions">
          <button
            className="lv-approve-btn"
            onClick={() => onApprove(leave.id)}
            disabled={approving === leave.id}
          >
            {approving === leave.id ? '…' : '✓ Approve'}
          </button>
          <button
            className="lv-reject-btn"
            onClick={() => onReject(leave)}
            disabled={approving === leave.id}
          >
            ✕ Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function HRLeaves() {
  const [leaves, setLeaves]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [cityFilter, setCityFilter]   = useState('All Cities');
  const [searchQuery, setSearchQuery] = useState('');
  const [approving, setApproving]     = useState(null);
  const [rejectLeave, setRejectLeave] = useState(null);
  const [toast, setToast]             = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getAllLeaves();
      setLeaves(res.leaves || []);
      setError(null);
    } catch (err) {
      setError('Failed to load leave requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleApprove = async (id) => {
    setApproving(id);
    try {
      const res = await api.approveLeave(id);
      if (res.error) { showToast('Error: ' + res.error); return; }
      showToast('Leave approved successfully.');
      fetchData();
    } catch {
      showToast('Failed to approve. Try again.');
    } finally {
      setApproving(null);
    }
  };

  const handleReject = async (id, reason) => {
    try {
      const res = await api.rejectLeave(id, reason);
      if (res.error) { showToast('Error: ' + res.error); return; }
      showToast('Leave rejected.');
      setRejectLeave(null);
      fetchData();
    } catch {
      showToast('Failed to reject. Try again.');
    }
  };

  // Split pending vs history
  const pending  = leaves.filter(l => l.status === 'pending');
  const history  = leaves.filter(l => l.status !== 'pending');

  // Apply filters to history
  let filtered = [...history];
  if (statusFilter !== 'all') filtered = filtered.filter(l => l.status === statusFilter);
  if (cityFilter !== 'All Cities') filtered = filtered.filter(l => l.city === cityFilter);
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(l =>
      (l.full_name   || '').toLowerCase().includes(q) ||
      (l.emp_code    || '').toLowerCase().includes(q) ||
      (l.leave_type  || '').toLowerCase().includes(q) ||
      (l.location_name || '').toLowerCase().includes(q)
    );
  }

  const approvedCount = leaves.filter(l => l.status === 'approved').length;
  const rejectedCount = leaves.filter(l => l.status === 'rejected').length;

  return (
    <div className="lv-root">

      {/* Toast */}
      {toast && <div className="lv-toast">{toast}</div>}

      {/* Header */}
      <div className="lv-header">
        <div className="lv-header-left">
          <h1 className="lv-title">Leave Requests</h1>
          <span className="lv-subtitle">
            {pending.length} pending · {approvedCount} approved · {rejectedCount} rejected
          </span>
        </div>
        <button className="lv-refresh-btn" onClick={fetchData}>↻ Refresh</button>
      </div>

      {error && <div className="lv-error">{error}</div>}

      {/* Pending Queue */}
      <div className="lv-section-label">
        Pending Approval
        {pending.length > 0 && (
          <span className="lv-pending-badge">{pending.length}</span>
        )}
      </div>

      {loading ? (
        <div className="lv-loading">
          <div className="lv-spinner" />
          <p>Loading leave requests…</p>
        </div>
      ) : pending.length === 0 ? (
        <div className="lv-empty-pending">
          ✓ No pending leave requests
        </div>
      ) : (
        <div className="lv-pending-list">
          {pending.map(leave => (
            <PendingCard
              key={leave.id}
              leave={leave}
              onApprove={handleApprove}
              onReject={setRejectLeave}
              approving={approving}
            />
          ))}
        </div>
      )}

      {/* History */}
      <div className="lv-section-label" style={{ marginTop: 28 }}>
        Leave History
      </div>

      <div className="lv-filters">
        <input
          className="lv-search"
          type="text"
          placeholder="Search by name, ID, leave type…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <div className="lv-filter-group">
          {STATUS_FILTERS.map(s => (
            <button
              key={s.key}
              className={`lv-filter-btn ${statusFilter === s.key ? 'lv-filter-active' : ''}`}
              onClick={() => setStatusFilter(s.key)}
            >{s.label}</button>
          ))}
        </div>
        <div className="lv-filter-group">
          {CITIES.map(c => (
            <button
              key={c}
              className={`lv-filter-btn ${cityFilter === c ? 'lv-filter-active' : ''}`}
              onClick={() => setCityFilter(c)}
            >{c}</button>
          ))}
        </div>
      </div>

      <div className="lv-table-wrap">
        {!loading && filtered.length === 0 ? (
          <div className="lv-empty">No leave records match your filters.</div>
        ) : (
          <table className="lv-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Type</th>
                <th>Dates</th>
                <th>Days</th>
                <th>Reason</th>
                <th>Location</th>
                <th>Status</th>
                <th>Actioned</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((leave, idx) => (
                <tr key={leave.id || idx}>
                  <td>
                    <div className="lv-emp-cell">
                      <div className={`lv-avatar ${getAvatarClass(leave.full_name)}`}>
                        {getInitials(leave.full_name)}
                      </div>
                      <div className="lv-emp-info">
                        <span className="lv-emp-name">{leave.full_name}</span>
                        <span className="lv-emp-code">{leave.emp_code}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`lv-type-tag ${LEAVE_TYPE_COLORS[leave.leave_type] || ''}`}>
                      {leave.leave_type?.charAt(0).toUpperCase() + leave.leave_type?.slice(1)}
                    </span>
                  </td>
                  <td>
                    <span className="lv-dates">{formatDateRange(leave.from_date, leave.to_date)}</span>
                  </td>
                  <td>
                    <span className="lv-days">{leave.total_days}</span>
                  </td>
                  <td>
                    <span className="lv-reason">{leave.reason || '—'}</span>
                  </td>
                  <td>
                    <div className="lv-loc-cell">
                      <span className="lv-loc-name">{leave.location_name || '—'}</span>
                      <span className="lv-city-tag">{leave.city || ''}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`lv-status-badge lv-status-${leave.status}`}>
                      {leave.status === 'approved' ? '✓ Approved' :
                       leave.status === 'rejected' ? '✕ Rejected' : leave.status}
                    </span>
                    {leave.status === 'rejected' && leave.rejection_reason && (
                      <div className="lv-rejection-reason">{leave.rejection_reason}</div>
                    )}
                  </td>
                  <td>
                    <span className="lv-actioned">{formatDate(leave.approved_at)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="lv-footer">
        Showing {filtered.length} of {history.length} historical records
      </div>

      {/* Reject modal */}
      {rejectLeave && (
        <RejectModal
          leave={rejectLeave}
          onConfirm={handleReject}
          onClose={() => setRejectLeave(null)}
        />
      )}
    </div>
  );
}